import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_GATEWAY_PATH, CheckoutGetPermissionResponse, RequestCreatePriceLead } from '@impact/data';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, throwError } from 'rxjs';
import { catchError, debounceTime, finalize, first, map, switchMap, take } from 'rxjs/operators';
import * as urltemplate from 'url-template';

import { ConsentService } from '../core/consent.service';
import { SettingsService } from '../core/settings.service';
import { FormService } from '../form/form.service';
import { MetaService } from '../meta/meta.service';
import { IIndicataResponse, INDICATA_RELS, INDICATA_RESPONSE_STEP_RELATION } from './indicata.models';

@Injectable({
    providedIn: 'root',
})
export class IndicataService {
    static ref = 'indicata';
    private indicataSequenceData$ = new BehaviorSubject<IIndicataResponse>({});
    private httpHeaders: HttpHeaders;
    private countryCode: string;
    private isTrackingEnabled = false;
    public indicataIsLoading$ = new BehaviorSubject<string | boolean>(false);
    public indicataErrorState$ = new BehaviorSubject<boolean>(false);
    public indicataRegistrationNumberErrorState$ = new BehaviorSubject<boolean>(false);
    public indicataSequenceLeadSubmitted$ = new BehaviorSubject<boolean>(false);
    get indicataSequenceData() {
        return this.indicataSequenceData$.asObservable();
    }

    private globalConsent: CheckoutGetPermissionResponse;
    private currentUrl: string;

    constructor(
        private readonly http: HttpClient,
        private readonly translateService: TranslateService,
        private readonly settingsService: SettingsService,
        private readonly consentService: ConsentService,
        private readonly metaService: MetaService,
        private readonly formService: FormService
    ) {
        this.settingsService.get().subscribe(({ indicata }) => {
            this.httpHeaders = new HttpHeaders({
                'Accept-Language': 'da-DK',
                Accept: 'application/json',
                Authorization: `Basic ${btoa(`${indicata?.indicataUsername}:${indicata?.indicataPassword}`)}`,
            });
            this.countryCode = 'DK';
        });

        // Get global consent
        this.consentService
            .getCheckoutConsentText()
            .pipe(first())
            .subscribe((consent) => {
                this.globalConsent = consent;
            });

        this.currentUrl = this.metaService.getCurrentUrl() ?? '';
    }
    startSequence(withLeadGeneration = false) {
        this.resetSequenceState();
        this.indicataIsLoading$.next(true);
        this._baseResource(INDICATA_RELS.SEQUENCE).subscribe(
            (tmpl) => {
                this.fetchSequenceData(tmpl.expand({ country: this.countryCode }));
            },
            () => this.handleError()
        );

        this.isTrackingEnabled = withLeadGeneration;

        if (this.isTrackingEnabled) {
            this.formService.trackInitialization(IndicataService.ref);
        }
    }

    goToStep(url?: string) {
        if (url) {
            this.fetchSequenceData(url);
        }
    }

    goToValuation(odometer: string) {
        this.indicataErrorState$.next(false);
        this.indicataIsLoading$.next(true);
        const params = {
            profiles: ['MAX_PURCHASE_PRICE_100'],
            odometer,
        };
        const { valuation, identifier } = this.indicataSequenceData$.value;
        if (valuation?.[0]?.href) {
            const [{ href }] = valuation;
            const url = urltemplate.parse(href).expand(params);
            this.http
                .get<IIndicataResponse>(url, { headers: this.httpHeaders })
                .pipe(
                    catchError((err) => {
                        return throwError(err);
                    })
                )
                .subscribe(
                    (res) => this.setSequenceData({ ...res, identifier, valuationUrl: url }),
                    () => this.handleError()
                );
        }
    }

    handleError() {
        this.indicataIsLoading$.next(false);
        this.indicataErrorState$.next(true);
    }

    resetSequenceState() {
        this.indicataSequenceData$.next({});
        this.indicataErrorState$.next(false);
        this.indicataRegistrationNumberErrorState$.next(false);
        this.indicataSequenceLeadSubmitted$.next(false);
    }

    bootstrapSequenceWithRegistrationNumber(registrationNumber: string) {
        this._baseResource(INDICATA_RELS.REGISTRATION_NUMBER)
            .pipe(
                catchError((err) => {
                    return throwError(err);
                })
            )
            .subscribe(
                (tmpl) => {
                    this.fetchSequenceData(
                        tmpl.expand({ country: this.countryCode, reg: registrationNumber }),
                        this.handleInvalidRegistrationNumber.bind(this)
                    );
                },
                () => this.handleError()
            );
    }

    private handleInvalidRegistrationNumber() {
        this.indicataIsLoading$.next(false);
        this.indicataRegistrationNumberErrorState$.next(true);
    }

    private setSequenceData(data: IIndicataResponse) {
        this.indicataSequenceData$.next(data);
        this.indicataIsLoading$.next(false);
    }

    private fetchSequenceData(url: string, errorHandling?: () => void) {
        this.indicataRegistrationNumberErrorState$.next(false);
        this.indicataErrorState$.next(false);
        this.indicataIsLoading$.next(url);
        this.http
            .get(url, { headers: this.httpHeaders })
            .pipe(
                catchError((err) => {
                    return throwError(err);
                })
            )
            .subscribe(
                (res) => this.setSequenceData(res),
                () => (errorHandling ? errorHandling() : this.handleError())
            );
    }

    private _baseResource(relation: INDICATA_RELS) {
        return this.http
            .get('https://ws.indicata.com/vivi/v2', {
                headers: this.httpHeaders,
            })
            .pipe(
                map((res: any) => {
                    const urlTemplate = res?.links?.filter((l: any) => l.rel === relation)[0].href;
                    return urltemplate.parse(urlTemplate);
                }),
                catchError((err) => {
                    return throwError(err);
                })
            );
    }

    submitSequenceLeads(leads: RequestCreatePriceLead) {
        this.indicataIsLoading$.next(true);

        const { valuations, make, regDate, model, body, trim, engine, odometer, overallCondition, valuationUrl } = this.indicataSequenceData$.value;
        const valuationAmount = valuations?.[0]?.amount;
        const valuationAmountValue = valuationAmount?.value || 0;
        const year = regDate?.summary?.split('-')?.[0];
        const month = regDate?.summary?.split('-')?.[1];
        const brandModel = model?.summary;
        const bodyVariant = body?.summary;
        const trimVariant = trim?.summary;
        const engineVariant = engine?.summary;
        const fuelType = engine?.description?.find((d) => d.label === 'fuel_type');

        const reqBody: RequestCreatePriceLead = {
            ...leads,
            indicataPrice: valuationAmountValue,
            brand: make?.summary,
            registrationYear: year ? parseInt(year) : undefined,
            registrationMonth: month ? parseInt(month) : undefined,
            model: brandModel,
            variant: `${trimVariant}, ${bodyVariant}, ${engineVariant}`,
            kilometers: odometer?.distance,
            motor: engineVariant,
            fuel: fuelType?.value,
            overallCondition: overallCondition?.grade,
            indicataUrl: valuationUrl,
            browserUrl: this.currentUrl ?? null,
            consentRevisionAcceptedGuid: this.globalConsent.revisionGuid ?? null,
        };

        return this.http
            .post(`${API_GATEWAY_PATH}/SidePanel/CreatePriceCheckLead`, reqBody)
            .pipe(
                // Fake spinning of 3 seconds, requested by customer
                debounceTime(3000),
                finalize(() => {
                    this.indicataSequenceLeadSubmitted$.next(true);
                    this.indicataIsLoading$.next(false);
                })
            )
            .pipe(
                catchError(() => {
                    this.submitSequenceLeadsCompletionTracking(reqBody);
                    return EMPTY;
                })
            )
            .toPromise()
            .then((data) => {
                if (this.isTrackingEnabled) {
                    this.submitSequenceLeadsCompletionTracking(reqBody);
                }

                return data;
            });
    }

    private submitSequenceLeadsCompletionTracking(reqBody: RequestCreatePriceLead) {
        this.formService.trackCompletion(
            IndicataService.ref,
            !!reqBody.consentRevisionAcceptedGuid,
            {
                make: reqBody.brand || '',
                model: reqBody.model || '',
            },
            {
                year: reqBody.registrationYear,
                month: reqBody.registrationMonth,
                variant: reqBody.variant,
                valuation: reqBody.indicataPrice,
                overallCondition: reqBody.overallCondition?.toString(),
            }
        );
    }

    getSectionHeader(step: INDICATA_RESPONSE_STEP_RELATION) {
        const translationsString = `indicata.section_header____${step.toLowerCase()}`;
        return this.translateService.get([translationsString]).pipe(
            take(1),
            map((translations: { [key: string]: string }) => translations[translationsString])
        );
    }

    // If you need to look-up a license plate, e.g.
    public independentIndicataFetch(licensePlate: string) {
        return this._baseResource(INDICATA_RELS.REGISTRATION_NUMBER).pipe(
            switchMap((tmpl) =>
                this.http.get<IIndicataResponse>(tmpl.expand({ country: this.countryCode, reg: licensePlate }), { headers: this.httpHeaders })
            )
        );
    }
}
